import { FeatureCollection, Feature } from 'geojson';
import axios, { AxiosError, AxiosInstance } from 'axios';

export interface BoundaryItem {
  name: string;
  type?: 'city' | 'state';
}

export interface BoundariesRequest {
  items: (string | BoundaryItem)[];
  defaults?: {
    type?: 'city' | 'state';
    countryCode?: string;
  };
}

interface BoundaryResult {
  input: {
    name: string;
    type: string;
    countryCode: string;
  };
  status: 'ok' | 'error';
  place?: any;
  geojson?: Feature;
  error?: string;
}

interface BoundariesResponse {
  results: BoundaryResult[];
}

export async function fetchBoundaries(
  request: BoundariesRequest
): Promise<FeatureCollection | null> {
  try {
    const response = await axios.post(`/boundaries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.data.success) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data: BoundariesResponse = await response.data.data;

    const features: Feature[] = data.results
      .filter(result => result.status === 'ok' && result.geojson)
      .map(result => result.geojson!);
    
    for (let a = 0; a < features.length; a++) {
      try {
        features[a] = JSON.parse(features[a].toString());
      } catch (e) { console.log(e) };
    }
    if (features.length === 0) {
      console.warn('No valid boundaries found in response');
      return null;
    }

    const featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    return featureCollection;
  } catch (error) {
    console.error('Error fetching boundaries:', error);
    return null;
  }
}

export async function fetchCityBoundaries(
  cityNames: string[],
  countryCode: string = 'BR'
): Promise<FeatureCollection | null> {
  const request: BoundariesRequest = {
    items: cityNames.map(name => ({ name, type: 'city' as const })),
    defaults: {
      countryCode,
    },
  };

  return fetchBoundaries(request);
}

export async function fetchStateBoundaries(
  stateNames: string[],
  countryCode: string = 'BR'
): Promise<FeatureCollection | null> {
  const request: BoundariesRequest = {
    items: stateNames.map(name => ({ name, type: 'state' as const })),
    defaults: {
      countryCode,
    },
  };

  return fetchBoundaries(request);
}